import { useState, useEffect } from "react";
import Header from "./Header/Header";
import { useSelector, useDispatch } from "react-redux";
import Banner from "./Banner/Banner";
import MainContainer from "./HeroSectionWidget/MainContainer/MainContainer";
import FindUsInYourTown from "./FindUsInYourTown/FindUsInYourTown";
import AboutUs from "./AboutUs/AboutUs";
import OurServices from "./OurServices/OurServices";
import Footer from "./Footer/Footer";
import CopyRight from "./CopyRight/CopyRight";
import Gallery from "./Gallery/Gallery";
import CustomerReviews from "./CustomerReviews/CustomerReviews";
import {
  setCitiesInfo,
  setWebConfig,
} from "../../../redux/actions/webConfigActions";
import {
  locationCodeMapSelector,
  serviceAvailableAtCitiesSelector,
} from "../../../redux/selectors/webConfigSelector";
import { fetchBlogs, fetchWebConfig } from "../../../apis/ConfigAPI";
import { setBlogs } from "../../../redux/actions/blogsActions";
import { useSessionManagement } from "../../../tracking/eventTracker";
import { Helmet, HelmetProvider } from "react-helmet-async";
import seoStrings from "../../../seoSrings";
import { resetSelectedTheaterDate } from "../../../redux/actions/bookingActions";
import { Organization } from "schema-dts";
import { helmetJsonLdProp } from "react-schemaorg";
import HowItWorks from "./HowItWorks/HowItWorks";
import HaveDoubts from "./HaveDoubts/HaveDoubts";
import VideoPopup from "./videoPopup";
import SearchTheatresSection from "./HeroSectionWidget/SearchTheatresSection/SearchTheatresSection";
import HeroSectionWidget from "./HeroSectionWidget/HeroSectionWidget";
import FAQSection from "./FAQSection/FAQSection";
import FloatingCards from "./FloatingCards/FloatingCards";
import LocationShowCase from "./LocationShowCase/LocationShowCase";
import PrivateTheatreContainer from "./PrivateTheatreContainer/PrivateTheatreContainer";

const Home = () => {
  const dispatch = useDispatch();
  const serviceAvailableAtCities =
    useSelector(serviceAvailableAtCitiesSelector) || [];
  const locationCodeMap = useSelector(locationCodeMapSelector) || {};
  const [error, setError] = useState("");

  // logging and monitoring
  useSessionManagement("HOME");

  useEffect(() => {
    if ((window as any).__PRELOADED_STATE__) {
      const webConfig = (window as any).__PRELOADED_STATE__.webConfig;
      const blogs = (window as any).__PRELOADED_STATE__.blogs;
      if (webConfig) dispatch(setWebConfig(webConfig));
      if (blogs) dispatch(setBlogs(blogs));
    } else {
      dispatch(resetSelectedTheaterDate());
      fetchWebConfig()
        .then((response) => {
          dispatch(setWebConfig(response));
        })
        .catch((error) => {
          setError(error);
        });

      fetchBlogs()
        .then((response) => {
          dispatch(setBlogs(response));
        })
        .catch((error) => {
          setError(error);
        });
    }
  }, []);

  useEffect(() => {
    dispatch(resetSelectedTheaterDate());
    fetchWebConfig()
      .then((response) => {
        dispatch(setWebConfig(response));
      })
      .catch((error) => {
        setError(error);
      });

    fetchBlogs()
      .then((response) => {
        dispatch(setBlogs(response));
      })
      .catch((err) => {
        setError(err);
      });
  }, []);

  useEffect(() => {
    let citiesInfo: any = {};
    for (let i = 0; i < serviceAvailableAtCities.length; i++) {
      let cityName = serviceAvailableAtCities[i]?.cityName;
      if (cityName in citiesInfo) {
        citiesInfo[cityName] = [...citiesInfo[cityName].locations];
      } else {
        citiesInfo[cityName] = [...serviceAvailableAtCities[i].locations];
      }
    }

    for (let city in citiesInfo) {
      let locations = citiesInfo[city];
      for (let i = 0; i < locations.length; i++) {
        locations[i] = {
          ...locations[i],
          locationCode: locationCodeMap[locations[i].locationName],
        };
      }
    }
    dispatch(setCitiesInfo({ citiesInfo: citiesInfo }));
  }, [serviceAvailableAtCities, locationCodeMap]);

  useEffect(() => {
    // Function to dynamically load a script
    const loadScript = (src = "", async = true, defer = false) => {
      const script = document.createElement("script");
      script.src = src;
      script.async = async;
      script.defer = defer;
      script.charset = "UTF-8";
      document.body.appendChild(script);
    };

    // Load scripts dynamically
    loadScript("https://featurable.com/assets/bundle.js", true, true);
    loadScript("//www.instagram.com/embed.js", true);
    loadScript("https://cdn.lightwidget.com/widgets/lightwidget.js");

    return () => {
      // Clean up: remove scripts if needed
      document
        .querySelectorAll(
          "script[src*='featurable.com'], script[src*='instagram.com'], script[src*='lightwidget.com']"
        )
        .forEach((script) => script.remove());
    };
  }, []);

  return (
    <>
      <HelmetProvider>
        <Helmet
          script={[
            helmetJsonLdProp<Organization>({
              description:
                "The Binge Town is a private theatre celebration venue chain based in India, offering a unique and luxurious experience for events and parties in Bengaluru, Hyderabad, and Delhi.",
              logo: "https://www.thebingetown.com/TBT.jpeg",
              "@context": "https://schema.org",
              "@type": "Organization",
              name: "The Binge Town",
              url: "https://thebingetown.com",
              contactPoint: {
                "@type": "ContactPoint",
                telephone: "+91-8618976974",
                contactType: "Customer Service",
                areaServed: "IN",
                availableLanguage: "English",
              },
              address: {
                "@type": "PostalAddress",
                streetAddress:
                  "2nd Floor, 10/1B, Graphite India Road Hoodi Village, KR Puram, Whitefield, 560048",
                addressLocality: "Bengaluru",
                addressCountry: "IN",
              },
              department: [
                {
                  "@type": "Organization",
                  name: "Koramangala",
                  address: {
                    "@type": "PostalAddress",
                    streetAddress:
                      "2nd Floor, SM Complex, Hosur Rd, Madiwala Koramangala 2nd Block, 560068",
                    addressLocality: "Bengaluru",
                    addressRegion: "Karnataka",
                    addressCountry: "IN",
                  },
                  contactPoint: {
                    "@type": "ContactPoint",
                    telephone: "+91-8618976974",
                    contactType: "Customer Service",
                    areaServed: "IN",
                    availableLanguage: "English",
                  },
                },
                {
                  "@type": "Organization",
                  name: "Rajaji Nagar",
                  address: {
                    "@type": "PostalAddress",
                    streetAddress:
                      "1st Floor, Shree Laxmi, Ram Mandir Road 59th Cross Road, 4th Block, Rajajinagar, 560010",
                    addressLocality: "Bengaluru",
                    addressRegion: "Karnataka",
                    addressCountry: "IN",
                  },
                  contactPoint: {
                    "@type": "ContactPoint",
                    telephone: "+91-8618976974",
                    contactType: "Customer Service",
                    areaServed: "IN",
                    availableLanguage: "English",
                  },
                },
                {
                  "@type": "Organization",
                  name: "Banashankari",
                  address: {
                    "@type": "PostalAddress",
                    streetAddress:
                      "1st Floor, SLV Arcade, Kathriguppe Signal Banashankari 3rd Stage, 560085",
                    addressLocality: "Bengaluru",
                    addressRegion: "Karnataka",
                    addressCountry: "IN",
                  },
                  contactPoint: {
                    "@type": "ContactPoint",
                    telephone: "+91-8618976974",
                    contactType: "Customer Service",
                    areaServed: "IN",
                    availableLanguage: "English",
                  },
                },
                {
                  "@type": "Organization",
                  name: "Whitefield",
                  address: {
                    "@type": "PostalAddress",
                    streetAddress:
                      "2nd Floor, 10/1, Graphite India Main Rd Hoodi, Whitefield, 560048",
                    addressLocality: "Bengaluru",
                    addressRegion: "Karnataka",
                    addressCountry: "IN",
                  },
                  contactPoint: {
                    "@type": "ContactPoint",
                    telephone: "+91-8618976974",
                    contactType: "Customer Service",
                    areaServed: "IN",
                    availableLanguage: "English",
                  },
                },

                {
                  "@type": "Organization",
                  name: "Nagavara",
                  address: {
                    "@type": "PostalAddress",
                    streetAddress:
                      " 3rd Floor, FJ Complex, 3rd Cross Rd Mariyannapalya, Nagavara, 560025",
                    addressLocality: "Bengaluru",
                    addressRegion: "Karnataka",
                    addressCountry: "IN",
                  },
                  contactPoint: {
                    "@type": "ContactPoint",
                    telephone: "+91-8618976974",
                    contactType: "Customer Service",
                    areaServed: "IN",
                    availableLanguage: "English",
                  },
                },
                {
                  "@type": "Organization",
                  name: "Jubilee Hills",
                  address: {
                    "@type": "PostalAddress",
                    streetAddress:
                      "1st Floor, Plot #769, Rd Number 44 CBI Colony, Jubilee Hills, 500033",
                    addressLocality: "Hyderabad",
                    addressRegion: "Telangana",
                    addressCountry: "IN",
                  },
                  contactPoint: {
                    "@type": "ContactPoint",
                    telephone: "+91-8618976974",
                    contactType: "Customer Service",
                    areaServed: "IN",
                    availableLanguage: "English",
                  },
                },
                {
                  "@type": "Organization",
                  name: "Himayatnagar",
                  address: {
                    "@type": "PostalAddress",
                    streetAddress:
                      "3rd Floor, Above Hot Tracks, Near Cafe Bahar Raja Reddy Marg, Himayatnagar, 500029",
                    addressLocality: "Hyderabad",
                    addressRegion: "Telangana",
                    addressCountry: "IN",
                  },
                  contactPoint: {
                    "@type": "ContactPoint",
                    telephone: "+91-8618976974",
                    contactType: "Customer Service",
                    areaServed: "IN",
                    availableLanguage: "English",
                  },
                },
                {
                  "@type": "Organization",
                  name: "Miyapur",
                  address: {
                    "@type": "PostalAddress",
                    streetAddress:
                      "1st Floor, Annapurna Enclave, 2-83, NH 65, Above Bandhan Bank Chanda Nagar, Miyapur, 500050",
                    addressLocality: "Hyderabad",
                    addressRegion: "Telangana",
                    addressCountry: "IN",
                  },
                  contactPoint: {
                    "@type": "ContactPoint",
                    telephone: "+91-8618976974",
                    contactType: "Customer Service",
                    areaServed: "IN",
                    availableLanguage: "English",
                  },
                },
                {
                  "@type": "Organization",
                  name: "Suchitra Circle",
                  address: {
                    "@type": "PostalAddress",
                    streetAddress:
                      "1st Floor, Plot #20, Venkateshwara Colony, Besides Anutex Suchitra Rd, 500067",
                    addressLocality: "Hyderabad",
                    addressRegion: "Telangana",
                    addressCountry: "IN",
                  },
                  contactPoint: {
                    "@type": "ContactPoint",
                    telephone: "+91-8618976974",
                    contactType: "Customer Service",
                    areaServed: "IN",
                    availableLanguage: "English",
                  },
                },
                {
                  "@type": "Organization",
                  name: "Janakpuri",
                  address: {
                    "@type": "PostalAddress",
                    streetAddress:
                      "3rd Floor, #3, Opp. Mata Chanan Devi Hospital Block C1, Janakpuri, 110058",
                    addressLocality: "Delhi",
                    addressRegion: "Delhi",
                    addressCountry: "IN",
                  },
                  contactPoint: {
                    "@type": "ContactPoint",
                    telephone: "+91-8618976974",
                    contactType: "Customer Service",
                    areaServed: "IN",
                    availableLanguage: "English",
                  },
                },
                {
                  "@type": "Organization",
                  name: "Laxmi Nagar",
                  address: {
                    "@type": "PostalAddress",
                    streetAddress:
                      "3rd Floor, D-32, near Laxmi Nagar Metro Station, above Domino's Laxmi Nagar, 110092",
                    addressLocality: "Delhi",
                    addressRegion: "Delhi",
                    addressCountry: "IN",
                  },
                  contactPoint: {
                    "@type": "ContactPoint",
                    telephone: "+91-8618976974",
                    contactType: "Customer Service",
                    areaServed: "IN",
                    availableLanguage: "English",
                  },
                },
                {
                  "@type": "Organization",
                  name: "Saket",
                  address: {
                    "@type": "PostalAddress",
                    streetAddress:
                      "Ground Floor, K261, Lane No 5, Westend Marg Saket, New Delhi, 110030",
                    addressLocality: "Delhi",
                    addressRegion: "Delhi",
                    addressCountry: "IN",
                  },
                  contactPoint: {
                    "@type": "ContactPoint",
                    telephone: "+91-8618976974",
                    contactType: "Customer Service",
                    areaServed: "IN",
                    availableLanguage: "English",
                  },
                },
                {
                  "@type": "Organization",
                  name: "Karol Bagh",
                  address: {
                    "@type": "PostalAddress",
                    streetAddress:
                      "1st Floor, 3C/8, New Rohtak Rd, opp. Liberty Cinemas Block 3C, Karol Bagh, New Delhi, 110005",
                    addressLocality: "Delhi",
                    addressRegion: "Delhi",
                    addressCountry: "IN",
                  },
                  contactPoint: {
                    "@type": "ContactPoint",
                    telephone: "+91-8618976974",
                    contactType: "Customer Service",
                    areaServed: "IN",
                    availableLanguage: "English",
                  },
                },
              ],
              foundingDate: "2020-22-01",
              sameAs: [
                "https://www.facebook.com/thebingetown",
                "https://x.com/thebingetown",
                "https://www.instagram.com/bingetown_in/",
                "https://www.linkedin.com/company/the-binge-town/",
              ],
            }),
          ]}
        >
          <title>{seoStrings?.["home"]?.["title"]}</title>
          <meta
            name="description"
            content={seoStrings?.["home"]?.["description"]}
          />
          <meta name="keywords" content={seoStrings["home"]["keyword"]} />
        </Helmet>
      </HelmetProvider>
      <Header />
      <VideoPopup />
      <Banner />
      {/*<HeroSectionWidget />*/}
      <MainContainer />
      {/*<FindUsInYourTown /> */}
      <FloatingCards />
      <AboutUs />
      <LocationShowCase />
      <PrivateTheatreContainer />
      <HowItWorks />
      <CustomerReviews />
      <FAQSection />
      <HaveDoubts />
      <OurServices />
      <Gallery />
      <Footer />
      <CopyRight />
    </>
  );
};
export default Home;
